import * as React from 'react';
import { Minus, Plus } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface NumberFieldProps {
  id?: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
  'aria-label'?: string;
}

const clamp = (n: number, min?: number, max?: number) => {
  let v = n;
  if (min !== undefined) v = Math.max(v, min);
  if (max !== undefined) v = Math.min(v, max);
  return v;
};

const decimalsOf = (step: number) => {
  const s = String(step);
  return s.includes('.') ? s.split('.')[1].length : 0;
};

// Press-and-hold timing: wait this long after the first step before repeating,
// then fire a step at this interval until the button is released.
const HOLD_DELAY_MS = 400;
const HOLD_INTERVAL_MS = 100;

/** Numeric input with +/- steppers. Flat, bordered, no motion — used everywhere the ROI page takes a number. */
export const NumberField = React.forwardRef<HTMLInputElement, NumberFieldProps>(
  ({ id, value, onChange, min, max, step = 1, prefix, suffix, className, ...aria }, ref) => {
    const decimals = decimalsOf(step);
    const round = (n: number) => Number(n.toFixed(decimals));

    // Keep the current value in a ref so the hold-repeat interval steps from the
    // latest value each tick instead of the stale value captured at press time.
    const valueRef = React.useRef(value);
    valueRef.current = value;

    const handleStep = (dir: 1 | -1) => onChange(clamp(round(valueRef.current + dir * step), min, max));

    const delayTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const repeatTimer = React.useRef<ReturnType<typeof setInterval> | null>(null);

    const stopRepeat = React.useCallback(() => {
      if (delayTimer.current) {
        clearTimeout(delayTimer.current);
        delayTimer.current = null;
      }
      if (repeatTimer.current) {
        clearInterval(repeatTimer.current);
        repeatTimer.current = null;
      }
    }, []);

    // pointerdown: one immediate step (matching a single click), then arm the
    // hold-to-repeat. The repeat halts itself the moment the value hits a bound,
    // so it never fires no-op updates once the button becomes disabled.
    const startRepeat = (dir: 1 | -1) => {
      handleStep(dir);
      delayTimer.current = setTimeout(() => {
        repeatTimer.current = setInterval(() => {
          const atBound =
            dir === 1 ? max !== undefined && valueRef.current >= max : min !== undefined && valueRef.current <= min;
          if (atBound) {
            stopRepeat();
            return;
          }
          handleStep(dir);
        }, HOLD_INTERVAL_MS);
      }, HOLD_DELAY_MS);
    };

    // Clear any running timer if the component unmounts mid-hold.
    React.useEffect(() => stopRepeat, [stopRepeat]);

    return (
      <div
        className={cn(
          // max-w keeps a stepper from stretching to the full width of a wide
          // grid cell — a 400px-wide box for a 3-digit number reads as broken.
          'flex h-9 w-full max-w-56 items-stretch overflow-hidden rounded-md border border-input bg-background focus-within:ring-1 focus-within:ring-ring',
          className,
        )}
      >
        <button
          type='button'
          tabIndex={-1}
          onPointerDown={() => startRepeat(-1)}
          onPointerUp={stopRepeat}
          onPointerLeave={stopRepeat}
          onPointerCancel={stopRepeat}
          onClick={(e) => {
            // Keyboard (Enter/Space) activation has detail === 0; the mouse/touch
            // press already stepped via onPointerDown, so only step here for keys.
            if (e.detail === 0) handleStep(-1);
          }}
          disabled={min !== undefined && value <= min}
          className='flex w-8 shrink-0 items-center justify-center text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-40'
          aria-label='Decrease'
        >
          <Minus className='size-3.5' />
        </button>
        {prefix && (
          <span className='flex items-center border-l border-input px-1.5 text-sm text-muted-foreground'>{prefix}</span>
        )}
        <input
          ref={ref}
          id={id}
          type='number'
          inputMode='decimal'
          value={Number.isFinite(value) ? value : ''}
          step={step}
          min={min}
          max={max}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              onChange(0);
              return;
            }
            const parsed = Number(raw);
            if (Number.isFinite(parsed)) onChange(clamp(parsed, min, max));
          }}
          className='w-full min-w-0 flex-1 border-l border-input bg-transparent px-2 text-center text-sm tabular-nums focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
          {...aria}
        />
        {suffix && (
          <span className='flex items-center border-l border-input px-1.5 text-sm text-muted-foreground'>{suffix}</span>
        )}
        <button
          type='button'
          tabIndex={-1}
          onPointerDown={() => startRepeat(1)}
          onPointerUp={stopRepeat}
          onPointerLeave={stopRepeat}
          onPointerCancel={stopRepeat}
          onClick={(e) => {
            if (e.detail === 0) handleStep(1);
          }}
          disabled={max !== undefined && value >= max}
          className='flex w-8 shrink-0 items-center justify-center border-l border-input text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-40'
          aria-label='Increase'
        >
          <Plus className='size-3.5' />
        </button>
      </div>
    );
  },
);
NumberField.displayName = 'NumberField';
